/**
 * Hatch fills for Leaflet vector layers — a `<pattern>` def registered on the SVG renderer.
 *
 * ## Why a pattern rather than another dash
 *
 * Two boundary hierarchies can be on one map at once (a §F5.8 watershed filter draws the
 * watershed **and** its partnerships), and until this landed both said "I am an area" with a
 * dashed outline and a flat 6–7 % wash. Two dash rhythms over two washes of similar weight is
 * the clash the client reported: nothing tells the eye which line belongs to which hierarchy,
 * and the two washes multiply into a third colour that is neither.
 *
 * The fix is to make them different *kinds* of mark rather than two settings of one mark: the
 * watershed is an **area** (flat tint, firm solid outline) and the partnership is an
 * **overlay** on top of it (diagonal hatch, thin solid outline). A hatch is transparent
 * between its lines, so whatever it covers — the watershed tint, a choropleth — still reads
 * through it, which is exactly what an overlay should do and what a second flat wash cannot.
 *
 * ## The mechanics
 *
 * Leaflet writes `options.fillColor` straight into the path's `fill` attribute, and SVG
 * accepts a paint server reference there — so `fillColor: 'url(#svp-hatch-partnership)'` is
 * all the layer needs, provided a `<pattern>` with that id exists in the document. This module
 * puts it in the renderer's own `<svg>` (the one Leaflet creates inside the pane), so the def
 * lives and dies with the map rather than in some global chrome.
 *
 * `patternUnits="userSpaceOnUse"` is the load-bearing choice. The alternative,
 * `objectBoundingBox`, scales the pattern to each polygon — a small partnership would get a
 * dense hatch and a large one a coarse one, from the same style. In user space the hatch has
 * one spacing everywhere, in screen pixels, and pans with the geometry because Leaflet moves
 * the whole `<svg>` viewBox.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/** A diagonal hatch: parallel lines of one colour on a transparent ground. */
export interface HatchSpec {
  /** Element id, and therefore the `url(#…)` every layer using it names. */
  id: string;
  /** Line colour. */
  stroke: string;
  /** Distance between lines, in map pixels. */
  spacing: number;
  /** Line thickness, in map pixels. */
  width: number;
  /** Line alpha. The ground between the lines is always fully transparent. */
  opacity: number;
  /** Rotation in degrees; 45 is the usual "diagonal". */
  angle: number;
}

/** The `fill` value a layer sets to paint with the pattern of this id. */
export function patternUrl(id: string): string {
  return `url(#${id})`;
}

/**
 * Register `spec` on this `<svg>` if it is not already there, and return its `url(#…)`.
 *
 * Idempotent by id: every `<HierarchyLayer>` rebuild calls it, and the renderer keeps one
 * `<svg>` per pane for the life of the map, so the def is written once and then found. Safe
 * with a null element (a pane with no vector layer in it yet has no `<svg>`) — the caller
 * still gets the url, and the next call with a real element installs the def. Browsers
 * re-resolve a paint reference when the referenced element appears, so a path that was
 * created first still picks the hatch up.
 */
export function ensureHatchPattern(svg: SVGSVGElement | null | undefined, spec: HatchSpec): string {
  const url = patternUrl(spec.id);
  if (!svg || svg.querySelector(`pattern[id="${spec.id}"]`)) return url;

  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = document.createElementNS(SVG_NS, 'defs');
    // First child: a def must be in the tree before the paths that reference it are painted,
    // and it costs nothing to be tidy about it.
    svg.insertBefore(defs, svg.firstChild);
  }

  const pattern = document.createElementNS(SVG_NS, 'pattern');
  pattern.setAttribute('id', spec.id);
  pattern.setAttribute('patternUnits', 'userSpaceOnUse');
  pattern.setAttribute('width', String(spec.spacing));
  pattern.setAttribute('height', String(spec.spacing));
  pattern.setAttribute('patternTransform', `rotate(${spec.angle})`);

  // One vertical line per tile; `patternTransform` is what makes it diagonal. Drawn the full
  // height of the tile so consecutive tiles join into an unbroken line.
  const line = document.createElementNS(SVG_NS, 'line');
  line.setAttribute('x1', '0');
  line.setAttribute('y1', '0');
  line.setAttribute('x2', '0');
  line.setAttribute('y2', String(spec.spacing));
  line.setAttribute('stroke', spec.stroke);
  line.setAttribute('stroke-width', String(spec.width));
  line.setAttribute('stroke-opacity', String(spec.opacity));
  pattern.appendChild(line);
  defs.appendChild(pattern);

  return url;
}

/**
 * The same hatch as a CSS background, for a panel swatch.
 *
 * The §F5.8 Boundaries panel has to show the reader the mark they are about to switch on, and
 * a 15 px `<span>` cannot host a Leaflet renderer. One function so the two descriptions of the
 * hatch cannot drift: same colour, same angle, same spacing.
 */
export function hatchCss(spec: HatchSpec): string {
  const ink = `color-mix(in srgb, ${spec.stroke} ${Math.round(spec.opacity * 100)}%, transparent)`;
  return `repeating-linear-gradient(${spec.angle}deg, ${ink} 0 ${spec.width}px, transparent ${spec.width}px ${spec.spacing}px)`;
}
